import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';
import {
  ColumnDefinition,
  renderColumn,
  sortableColumn,
} from 'components/TableColumn/TableColumn';
import { useCallAPI } from 'hooks/useCallAPI';
import styles from './storedValueDetail.module.scss';
import { SelectOptions } from 'components/commonType';
import {
  DefaultStoredValueManager,
  StoredValueManager,
} from 'core/storedValue/StoredValueManager';
import i18n from 'i18n';
import { useParams } from 'react-router-dom';
import { StoredValueLog } from 'core/storedValue/StoredValueLog';
import { amountFormatter, priceFormatter } from './PriceFormatter';
import { defaultTo } from 'lodash';
import { Agency, DefaultAgencyManager } from 'core';

export enum StoredValueLogColumns {
  DATE = 'storedValueId',
  DESCRIPTION = 'description',
  COMMENT = 'comment',
  AMOUNT = 'amount',
  REMAIN = 'remain',
}

const defaultAgencyManager = new DefaultAgencyManager();
const defaultStoredValueManager = new DefaultStoredValueManager();

export enum StoredValueEditType {
  NONE = 'none',
  ADD = 'add',
  EDIT = 'edit',
}

type StoredValueInfo = SelectOptions & { className?: string };

export const useStoredValueDetailModel = (
  storedValueManager: StoredValueManager = defaultStoredValueManager,
  agencyManager = defaultAgencyManager,
) => {
  const params = useParams<{ agencyId: string }>();
  const agencyId: number | undefined = params.agencyId
    ? +params.agencyId
    : undefined;

  if (!agencyId) {
    throw new Error('Agency ID is required');
  }

  const { loading, callAPIs } = useCallAPI();
  const [storedValueLog, setStoredValueLog] = useState<StoredValueLog[]>([]);
  const [storedValueInfo, setStoredValueInfo] = useState<StoredValueInfo[]>([]);
  const [usedValueInfo, setUsedValueInfo] = useState<SelectOptions[]>([]);
  const [remainingValue, setRemainingValue] = useState<number>(0);
  const [agencyInfo, setAgencyInfo] = useState<{
    name: string;
    vendorNumber: string;
    currency: string;
  }>({
    name: '',
    vendorNumber: '',
    currency: 'NTD',
  });

  const [editType, setEditType] = useState<StoredValueEditType>(
    StoredValueEditType.NONE,
  );

  const initInfo = useCallback(
    (
      storedValueLog: StoredValueLog[],
      agencyInfo: {
        name: string;
        currency: string;
      },
    ) => {
      const storedValueSummary =
        storedValueManager.getStoredValueSummary(storedValueLog);
      const summaryOfAgency = defaultTo(
        storedValueSummary.find((item) => item.adAgencyId === agencyId),
        {
          currency: agencyInfo.currency,
          remain: 0,
          stored: 0,
          used: 0,
        },
      );

      setStoredValueInfo([
        {
          label: i18n.t<string>('storedValueDetail.labels.remainValue'),
          value: formatPriceWithCurrency(
            summaryOfAgency.currency,
            summaryOfAgency.remain,
          ),
          className: styles.remainValue,
        },
      ]);
      setUsedValueInfo([
        {
          label: i18n.t<string>('storedValueDetail.labels.usedValue'),
          value: formatPriceWithCurrency(
            summaryOfAgency.currency,
            summaryOfAgency.used,
          ),
        },
        {
          label: i18n.t<string>('storedValueDetail.labels.totalValue'),
          value: formatPriceWithCurrency(
            summaryOfAgency.currency,
            summaryOfAgency.stored,
          ),
        },
      ]);
      setRemainingValue(summaryOfAgency.remain);
    },
    [storedValueManager, agencyId],
  );

  const initData = useCallback(async () => {
    callAPIs(
      [
        () => storedValueManager.getStoredValueLogsByAgencyId(agencyId),
        () => agencyManager.fetchAgency(agencyId),
      ],
      (storedValueLogs, agency: Agency) => {
        const { detail } = agency;
        const agencyInfo = {
          name: detail.companyName,
          currency: detail.currency,
          vendorNumber: detail.vendorNumber,
        };
        setAgencyInfo(agencyInfo);
        setStoredValueLog(storedValueLogs);
        initInfo(storedValueLogs, agencyInfo);
      },
    );
  }, [storedValueManager, agencyManager, agencyId, callAPIs, initInfo]);

  useEffect(() => {
    // clear location state
    window.history.replaceState({}, '');
    initData();
  }, [initData]);

  const columnDefinition = (columnName): ColumnDefinition => ({
    ...sortableColumn(columnName, `storedValueLog.headers.${columnName}`, true),
    classes: () => styles[columnName],
    headerClasses: () => styles[columnName],
  });

  const columns = useMemo(
    () => [
      renderColumn(
        columnDefinition(StoredValueLogColumns.DATE),
        (_, rowData: StoredValueLog) => rowData.createTime,
      ),
      renderColumn(columnDefinition(StoredValueLogColumns.DESCRIPTION)),
      renderColumn(columnDefinition(StoredValueLogColumns.COMMENT)),
      renderColumn(
        columnDefinition(StoredValueLogColumns.REMAIN),
        priceFormatter,
      ),
      renderColumn(
        columnDefinition(StoredValueLogColumns.AMOUNT),
        amountFormatter,
      ),
    ],
    [],
  );

  const onEditValueModalClose = (refresh: boolean) => {
    setEditType(StoredValueEditType.NONE);
    if (refresh) {
      initData();
    }
  };

  return {
    agencyId,
    loading,
    columns,
    editType,
    storedValueLog,
    storedValueInfo,
    usedValueInfo,
    remainingValue,
    agencyInfo,
    onEditValueModalClose,
    setEditType,
  };
};
